import { DOCUMENT_LIBRARY_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { FormFieldListValue } from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { ListField } from '@dealstack/frontend/shared/components/Documents/FormFields/ViewField/ListField';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { MultiSelect, Button, Flex, Input } from '@mantine/core';
import { useState } from 'react';
import { usePersonsInDeal } from '@dealstack/frontend/shared/components/Documents/hooks/usePersonsInDeal';
import { useAddPersonsToADocument } from '@dealstack/frontend/shared/components/Documents/hooks/useAddPersonsToADocument';
import { useRemovePersonsFromDocument } from '@dealstack/frontend/shared/components/Documents/hooks/useRemovePersonsFromDocument';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	ListFieldContainer,
	ButtonWrapper,
} from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/styled';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	DocumentFieldFilter,
	DocumentLinkTarget,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { InvestorVisibilityToggle } from '@dealstack/frontend/shared/components/Documents/FormFields/DocumentFieldView/InvestorVisibilityToggle';

interface Props {
	items: FormFieldListValue[];
	dealId: string;
	documentId: string;
	variant: 'primary' | 'secondary';
	onCancel: () => void;
	target?: DocumentLinkTarget;
	dataOperations?: TableOperations<DocumentFieldFilter>;
	/**
	 * DC-494: investor-portal visibility keyed by person id. Only person links appear here, so a
	 * missing key means "this person came from a transaction" and no toggle is offered. The owner
	 * resolves the feature flag and omits this entirely when the flag is off, keeping this shared
	 * component free of flag lookups (and of the ConfigCatProvider its tests would then require).
	 */
	investorVisibilityByPersonId?: Record<string, boolean>;
}

/**
 * Component used to display and manage a list of persons associated with a document.
 * It allows adding and removing persons, filtering the list, and toggling between edit and view modes.
 */
export const PersonsListView = ({
	items,
	dealId,
	documentId,
	variant,
	onCancel,
	target,
	dataOperations,
	investorVisibilityByPersonId,
}: Props) => {
	const { data: persons } = usePersonsInDeal(dealId);
	const tCommonUi = useTranslationCommonUi();
	const { handleRemovePersons, isLoading: isRemoving } = useRemovePersonsFromDocument({
		dealId,
		documentId,
		target,
		dataOperations,
	});
	const { handleAddPersons, isLoading: isAdding } = useAddPersonsToADocument({
		dealId,
		documentId,
		target,
		dataOperations,
	});
	const isSaving = isRemoving || isAdding;
	const [filterName, setFilterName] = useState('');
	const [isEditing, setIsEditing] = useState(variant === 'secondary');
	const [isAddNew, setIsAddNew] = useState(false);
	const [personsToRemoveIds, setPersonsToRemoveIds] = useState<string[]>([]);
	const [personsToAdd, setPersonsToAdd] = useState<string[]>([]);
	const filteredItems = items
		.filter((item) => item.value.toLowerCase().includes(filterName.toLowerCase()))
		.filter((item) => !personsToRemoveIds.includes(item.id));

	const handleAddNew = () => {
		setIsAddNew(true);
	};

	const resetPendingEdits = () => {
		setIsEditing(false);
		setIsAddNew(false);
		setPersonsToRemoveIds([]);
		setPersonsToAdd([]);
	};

	const handleCancel = () => {
		resetPendingEdits();
		onCancel();
	};

	const handleRemovePerson = (item: FormFieldListValue) => {
		setPersonsToRemoveIds((prev) => [...prev, item.id]);
	};

	/**
	 * DC-1115: Save has to leave the edit state, not just drop the pending list. The secondary
	 * variant is an inline field whose edit flag lives in the parent, so `onCancel` is the only
	 * way to bring its read-only ViewField (and the pencil that reopens it) back — without this
	 * a saved removal left the field as a list with no controls at all. The primary variant *is*
	 * the panel, so it stays open and falls back to its own view mode.
	 */
	const leaveEditMode = () => {
		resetPendingEdits();
		if (variant === 'secondary') {
			onCancel();
		}
	};

	/**
	 * Nothing queued still means "saved": the investor-portal toggles persist on flip, so the
	 * list on screen already matches what is stored. Staying silent is what DC-1115 reported.
	 */
	const confirmNothingPending = () => {
		showToastV2({
			title: 'Success',
			message: 'Related people saved',
			state: 'success',
		});
	};

	/**
	 * Both Save buttons land here. Pending removals survive the switch into the add form, so a
	 * handler that submitted only its own queue would silently drop the other one.
	 */
	const submitPendingEdits = async () => {
		if (personsToRemoveIds.length === 0 && personsToAdd.length === 0) {
			confirmNothingPending();
			leaveEditMode();
			return;
		}

		// The hooks own both toasts; whatever fails stays queued so Save can be retried, and
		// whatever succeeded leaves the queue so the retry does not resubmit it.
		if (personsToRemoveIds.length > 0) {
			if (!(await handleRemovePersons(documentId, personsToRemoveIds))) {
				return;
			}
			setPersonsToRemoveIds([]);
		}

		if (personsToAdd.length > 0) {
			if (!(await handleAddPersons(documentId, personsToAdd))) {
				return;
			}
			setPersonsToAdd([]);
		}

		leaveEditMode();
	};

	return (
		<Flex
			data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST}
			h="100%"
			justify="space-between"
			direction="column"
			gap="0.5rem"
			w="100%"
		>
			<div>
				<ButtonWrapper>
					{!isAddNew && variant === 'primary' && (
						<Flex gap="0.5rem" align="center" direction="column" w="100%">
							<Input
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_FIND_INPUT}
								value={filterName}
								onChange={(e) => setFilterName(e.target.value)}
								placeholder="Find"
								width="100%"
								styles={{
									wrapper: { margin: '0px', width: '100%' },
									input: { margin: '0px' },
								}}
								leftSection={<Icon variant="search-v3" size="md" />}
								size="md"
							/>
						</Flex>
					)}

					{isAddNew && (
						<MultiSelect
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_ADD_SELECT}
							w="100%"
							onChange={setPersonsToAdd}
							value={personsToAdd}
							data={persons ?? []}
							label="Add person"
							placeholder="Find"
							searchable
							nothingFoundMessage="Nothing found"
							maxDropdownHeight={200}
						/>
					)}
				</ButtonWrapper>
				{!isAddNew && (
					<div style={{ width: '100%' }}>
						{filteredItems?.map((item) => (
							<ListFieldContainer key={item.id}>
								<ListField
									item={item}
									isEditing={isEditing}
									removeItem={handleRemovePerson}
								/>
								{investorVisibilityByPersonId?.[item.id] !== undefined && (
									<InvestorVisibilityToggle
										dealId={dealId}
										documentId={documentId}
										personId={item.id}
										visibleToInvestor={investorVisibilityByPersonId[item.id]}
									/>
								)}
							</ListFieldContainer>
						))}
					</div>
				)}
			</div>
			<Flex
				gap="0.5rem"
				align="center"
				direction={variant === 'primary' ? 'column' : 'row'}
				w="100%"
			>
				{isAddNew && (
					<>
						<Button
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_SAVE_BUTTON}
							loading={isSaving}
							disabled={isSaving}
							w={variant === 'primary' ? '100%' : 'auto'}
							variant="filled"
							onClick={submitPendingEdits}
						>
							{tCommonUi('common_buttons.save')}
						</Button>
						<Button
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_CANCEL_BUTTON}
							disabled={isSaving}
							w={variant === 'primary' ? '100%' : 'auto'}
							variant="secondary"
							onClick={handleCancel}
						>
							{tCommonUi('common_buttons.cancel')}
						</Button>
					</>
				)}

				{isEditing && !isAddNew && (
					<Flex justify="space-between" w="100%">
						{variant === 'secondary' && (
							<Button
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_ADD_ANOTHER}
								variant="white"
								style={{ flexShrink: 0 }}
								onClick={() => handleAddNew()}
							>
								{tCommonUi('common_buttons.add_another')}
							</Button>
						)}

						<Flex justify="flex-end" gap="0.5rem">
							<Button
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_SAVE_BUTTON}
								loading={isSaving}
								disabled={isSaving}
								w={variant === 'primary' ? '100%' : 'auto'}
								variant="filled"
								onClick={submitPendingEdits}
							>
								{tCommonUi('common_buttons.save')}
							</Button>
							<Button
								data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_CANCEL_BUTTON}
								disabled={isSaving}
								w={variant === 'primary' ? '100%' : 'auto'}
								variant="secondary"
								onClick={handleCancel}
							>
								{tCommonUi('common_buttons.cancel')}
							</Button>
						</Flex>
					</Flex>
				)}

				{!isEditing && !isAddNew && variant === 'primary' && (
					<>
						<Button
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_ADD_ANOTHER}
							w="100%"
							variant="filled"
							onClick={() => handleAddNew()}
						>
							{tCommonUi('common_buttons.add_another')}
						</Button>
						<Button
							data-testid={DOCUMENT_LIBRARY_ELEMENT_ID.PERSONS_LIST_EDIT_BUTTON}
							w="100%"
							variant="filled"
							styles={{ label: { color: 'black' } }}
							color="gray.4"
							onClick={() => setIsEditing(!isEditing)}
						>
							{tCommonUi('common_buttons.edit')}
						</Button>
					</>
				)}
			</Flex>
		</Flex>
	);
};
